import {
  ConfinusUI__Tables__DataTableHeaderRowControllerIdentifier,
  ConfinusUI__Tables__DataTableRowControllerIdentifier,
  ConfinusUI__Tables__BulkActionsBarControllerIdentifier,
} from "controllers/identifiers";
import BaseController from "confinus_ui/base_controller";
import DataTableHeaderRowController from "confinus_ui/tables/data_table_header_row_controller";
import DataTableRowController from "confinus_ui/tables/data_table_row_controller";
import BulkActionsBarController from "confinus_ui/tables/bulk_actions_bar_controller";
import SelectionPersistenceService from "confinus_ui/tables/selection_persistence_service";

export default class DataTableController extends BaseController {
  public static outlets = [
    ConfinusUI__Tables__DataTableHeaderRowControllerIdentifier,
    ConfinusUI__Tables__DataTableRowControllerIdentifier,
    ConfinusUI__Tables__BulkActionsBarControllerIdentifier,
  ];
  declare private readonly confinusUiTablesDataTableHeaderRowOutlet: DataTableHeaderRowController;
  declare private readonly hasConfinusUiTablesDataTableHeaderRowOutlet: boolean;
  private get headerRowController() {
    return this.confinusUiTablesDataTableHeaderRowOutlet;
  }
  private get hasHeaderRowController() {
    return this.hasConfinusUiTablesDataTableHeaderRowOutlet;
  }
  declare private readonly confinusUiTablesDataTableRowOutlets: DataTableRowController[];
  private get rowControllers() {
    return this.confinusUiTablesDataTableRowOutlets;
  }

  declare private readonly confinusUiTablesBulkActionsBarOutlet: BulkActionsBarController;
  declare private readonly hasConfinusUiTablesBulkActionsBarOutlet: boolean;
  private get bulkActionsBarController() {
    return this.confinusUiTablesBulkActionsBarOutlet;
  }

  public static targets = ["tableContentContainer", "tableBody"];

  declare private readonly tableContentContainerTarget: HTMLDivElement;
  declare private readonly tableBodyTarget: HTMLTableSectionElement;

  /**
   * Values for table identification and persistence.
   * tableId: Unique identifier for this table instance (e.g., 'entities_table', 'order_items_table')
   * persistSelections: When true, selections are saved to localStorage and restored on page reload
   */
  public static values = {
    tableId: { type: String, default: "" },
    persistSelections: { type: Boolean, default: false },
  };

  declare private readonly tableIdValue: string;
  declare private readonly persistSelectionsValue: boolean;

  // Track selection change listeners
  private selectionChangeListeners: Set<(selectedIds: string[]) => void> =
    new Set();
  private storageListenerCleanup: (() => void) | null = null;
  private persistedSelections: Set<string> = new Set();
  // Store bound event handler to fix memory leak
  private rowSelectionChangeHandler: ((event: Event) => void) | null = null;
  // Track retry attempts for applying persisted selections
  private applySelectionsRetryCount: number = 0;
  private static readonly MAX_APPLY_SELECTIONS_RETRIES = 3;
  // Debouncing for selection changes
  private selectionChangeDebounceTimer: number | null = null;
  private static readonly SELECTION_CHANGE_DEBOUNCE_MS = 150;
  // ResizeObserver for updating scroll shadows on layout changes
  private resizeObserver: ResizeObserver | null = null;

  public initialize() {
    super.initialize();
    this.contentScrolled();
  }

  public connect() {
    super.connect();

    // Load persisted selections if persistence is enabled
    if (this.persistSelectionsValue && this.tableIdValue) {
      // Register for quota exceeded notifications
      SelectionPersistenceService.onQuotaExceeded(
        this.handleQuotaExceeded.bind(this),
      );

      this.loadPersistedSelections();
      // Listen for cross-tab storage changes
      this.storageListenerCleanup =
        SelectionPersistenceService.addStorageListener(
          this.tableIdValue,
          this.handleStorageChange.bind(this),
        );
    }

    if (this.hasHeaderRowController) {
      // When a checkbox changes, toggle all rows.
      this.headerRowController.onCheckboxChange(this.toggleRows.bind(this));
    }

    // Set up connection with bulk actions bar if present
    if (this.hasConfinusUiTablesBulkActionsBarOutlet) {
      this.bulkActionsBarController.setDataTableController(this);
      // Pass table ID for persistence
      if (this.persistSelectionsValue && this.tableIdValue) {
        this.bulkActionsBarController.setTableId(this.tableIdValue);
      }
    }

    // Listen for individual row selection changes
    // Store bound handler to properly remove it later
    this.rowSelectionChangeHandler = this.handleRowSelectionChange.bind(this);
    this.element.addEventListener(
      "data-table-row-selection-changed",
      this.rowSelectionChangeHandler,
    );

    // Observe resize to update scroll shadows when layout changes
    this.resizeObserver = new ResizeObserver(() => this.contentScrolled());
    this.resizeObserver.observe(this.tableContentContainerTarget);
  }

  public disconnect() {
    this.selectionChangeListeners.clear();
    // Properly remove the event listener using the stored handler
    if (this.rowSelectionChangeHandler) {
      this.element.removeEventListener(
        "data-table-row-selection-changed",
        this.rowSelectionChangeHandler,
      );
      this.rowSelectionChangeHandler = null;
    }
    // Clean up storage listener
    if (this.storageListenerCleanup) {
      this.storageListenerCleanup();
      this.storageListenerCleanup = null;
    }
    // Clear any pending debounce timer
    if (this.selectionChangeDebounceTimer !== null) {
      clearTimeout(this.selectionChangeDebounceTimer);
      this.selectionChangeDebounceTimer = null;
    }
    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    super.disconnect();
  }

  /**
   * Load persisted selections from localStorage and apply them to visible rows.
   * Uses checkbox values as stable identifiers that persist across page loads.
   * Waits for next animation frame to ensure row controllers are initialized.
   */
  private loadPersistedSelections() {
    if (!this.persistSelectionsValue || !this.tableIdValue) {
      return;
    }

    const persistedIds = SelectionPersistenceService.loadSelections(
      this.tableIdValue,
    );
    this.persistedSelections = new Set(persistedIds);

    // Apply persisted selections to visible rows
    // Use requestAnimationFrame for more reliable timing than setTimeout
    this.applyPersistedSelections();
  }

  /**
   * Apply persisted selections to row controllers.
   * Uses requestAnimationFrame to ensure DOM is ready.
   * Includes retry limit to prevent infinite recursion.
   */
  private applyPersistedSelections() {
    // Double RAF pattern ensures outlets are connected and ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Verify row controllers are available
        if (this.rowControllers.length === 0) {
          // If no row controllers yet, retry with limit
          this.applySelectionsRetryCount++;
          if (
            this.applySelectionsRetryCount <
            DataTableController.MAX_APPLY_SELECTIONS_RETRIES
          ) {
            requestAnimationFrame(() => this.applyPersistedSelections());
          } else {
            console.warn(
              `Failed to apply persisted selections after ${DataTableController.MAX_APPLY_SELECTIONS_RETRIES} retries. Row controllers may not be initialized.`,
            );
          }
          return;
        }

        // Reset retry counter on success
        this.applySelectionsRetryCount = 0;

        this.rowControllers.forEach((controller) => {
          const rowId = controller.checkboxValue;
          if (rowId && this.persistedSelections.has(rowId)) {
            controller.checked = true;
          }
        });

        // Update header checkbox state
        this.updateHeaderCheckboxState();

        // Notify bulk actions bar about persisted selections
        if (this.persistedSelections.size > 0) {
          this.notifySelectionChange();
        }
      });
    });
  }

  /**
   * Handle storage changes from other tabs/windows.
   * Synchronizes selections across browser tabs in real-time.
   */
  private handleStorageChange(selectedIds: string[]) {
    // Update our persisted selections set
    this.persistedSelections = new Set(selectedIds);

    // Update visible rows to match
    this.rowControllers.forEach((controller) => {
      const rowId = controller.checkboxValue;
      if (rowId) {
        const shouldBeChecked = this.persistedSelections.has(rowId);
        if (controller.checked !== shouldBeChecked) {
          controller.checked = shouldBeChecked;
        }
      }
    });

    // Update header checkbox state
    this.updateHeaderCheckboxState();
    this.notifySelectionChange();
  }

  /**
   * Handle localStorage quota exceeded errors.
   * Provides user feedback and attempts recovery.
   */
  private handleQuotaExceeded(tableId: string, error: Error) {
    if (tableId !== this.tableIdValue) {
      return; // Not our table
    }

    console.error(
      `Storage quota exceeded for table ${tableId}. Clearing old selections.`,
      error,
    );

    // Dispatch a custom event that can be handled by the application
    // to show user feedback (e.g., toast notification)
    this.element.dispatchEvent(
      new CustomEvent("data-table-storage-quota-exceeded", {
        bubbles: true,
        detail: {
          tableId,
          message:
            "Selection storage limit reached. Some selections may not be saved.",
          error,
        },
      }),
    );

    // As a fallback, only keep selections for current page
    const visibleSelections = this.getVisibleSelectedRowIds();
    this.persistedSelections = new Set(visibleSelections);
    SelectionPersistenceService.saveSelections(tableId, visibleSelections);
  }

  /**
   * Handle individual row selection changes.
   * Updates persisted selections using checkbox values as stable identifiers.
   * Checkbox values persist across page loads unlike row element IDs.
   */
  private handleRowSelectionChange(event: Event) {
    const detail = (event as CustomEvent).detail;

    // Update persisted selections if enabled
    if (this.persistSelectionsValue && this.tableIdValue) {
      if (detail && detail.checkboxValue) {
        if (detail.checked) {
          this.persistedSelections.add(detail.checkboxValue);
        } else {
          this.persistedSelections.delete(detail.checkboxValue);
        }
      }
    }

    // Update header checkbox state
    this.updateHeaderCheckboxState();
    this.notifySelectionChange();
  }

  private updateHeaderCheckboxState() {
    if (this.hasHeaderRowController) {
      const allVisibleChecked = this.rowControllers.every((c) => c.checked);
      const someChecked = this.rowControllers.some((c) => c.checked);
      const hasPersistedSelections = this.persistedSelections.size > 0;

      if (allVisibleChecked && this.rowControllers.length > 0) {
        // All visible rows are checked
        this.headerRowController.checked = true;
        this.headerRowController.indeterminate = false;
      } else if (someChecked || hasPersistedSelections) {
        // Some rows are checked (either visible or persisted from other pages)
        this.headerRowController.checked = false;
        this.headerRowController.indeterminate = true;
      } else {
        // No rows are checked
        this.headerRowController.checked = false;
        this.headerRowController.indeterminate = false;
      }
    }
  }

  public toggleRows(checked: boolean) {
    this.rowControllers.forEach((controller) => {
      controller.checked = checked;
    });

    // Update persisted selections if enabled
    if (this.persistSelectionsValue && this.tableIdValue) {
      if (checked) {
        // Add all visible rows to persisted selections
        this.rowControllers.forEach((controller) => {
          const rowId = controller.checkboxValue;
          if (rowId) {
            this.persistedSelections.add(rowId);
          }
        });
      } else {
        // Remove all visible rows from persisted selections
        this.rowControllers.forEach((controller) => {
          const rowId = controller.checkboxValue;
          if (rowId) {
            this.persistedSelections.delete(rowId);
          }
        });
      }
    }

    this.notifySelectionChange();
  }

  /**
   * Get values of all selected rows including persisted selections from other pages.
   * When persistence is enabled, returns all selected items across all pages.
   * Otherwise returns only visible page selections.
   */
  public getSelectedRowIds(): string[] {
    if (this.persistSelectionsValue && this.tableIdValue) {
      // Return all persisted selections (includes items from other pages)
      return Array.from(this.persistedSelections);
    }

    // Fall back to only visible selections
    return this.rowControllers
      .filter((controller) => controller.checked)
      .map((controller) => controller.checkboxValue);
  }

  // Get only the currently visible selected row IDs
  public getVisibleSelectedRowIds(): string[] {
    return this.rowControllers
      .filter((controller) => controller.checked)
      .map((controller) => controller.checkboxValue);
  }

  // Get all selected row controllers
  public getSelectedRows(): DataTableRowController[] {
    return this.rowControllers.filter((controller) => controller.checked);
  }

  // Get count of selected rows including persisted selections
  public getSelectionCount(): number {
    if (this.persistSelectionsValue && this.tableIdValue) {
      return this.persistedSelections.size;
    }
    return this.rowControllers.filter((controller) => controller.checked)
      .length;
  }

  // Check if any rows are selected
  public hasSelection(): boolean {
    return this.getSelectionCount() > 0;
  }

  // Clear all selections
  public clearSelection() {
    this.rowControllers.forEach((controller) => {
      controller.checked = false;
    });
    if (this.hasHeaderRowController) {
      this.headerRowController.checked = false;
    }

    // Clear persisted selections if enabled
    if (this.persistSelectionsValue && this.tableIdValue) {
      this.persistedSelections.clear();
      SelectionPersistenceService.clearSelections(this.tableIdValue);
    }

    this.notifySelectionChange();
  }

  // Register a listener for selection changes
  public onSelectionChange(callback: (selectedIds: string[]) => void) {
    this.selectionChangeListeners.add(callback);
    return () => this.selectionChangeListeners.delete(callback);
  }

  // Notify all listeners of selection change with debouncing
  private notifySelectionChange() {
    // Clear existing timer
    if (this.selectionChangeDebounceTimer !== null) {
      clearTimeout(this.selectionChangeDebounceTimer);
    }

    // Debounce to avoid excessive localStorage writes on rapid selection changes
    this.selectionChangeDebounceTimer = window.setTimeout(() => {
      this.selectionChangeDebounceTimer = null;

      const selectedIds = this.getSelectedRowIds();

      // Persist selections if enabled
      if (this.persistSelectionsValue && this.tableIdValue) {
        SelectionPersistenceService.saveSelections(
          this.tableIdValue,
          selectedIds,
        );
      }

      this.selectionChangeListeners.forEach((listener) => {
        listener(selectedIds);
      });

      // If we have a bulk actions bar outlet, update it
      if (this.hasConfinusUiTablesBulkActionsBarOutlet) {
        this.bulkActionsBarController.handleSelectionChange(selectedIds);
      }
    }, DataTableController.SELECTION_CHANGE_DEBOUNCE_MS);
  }

  public appendRow(row: HTMLElement) {
    this.tableBodyTarget.appendChild(row);
    this.contentScrolled();
  }

  public contentScrolled() {
    const scrollContainer = this.tableContentContainerTarget;
    const shadowTarget = scrollContainer.parentElement;
    if (!shadowTarget) return;

    const x = scrollContainer.scrollLeft;
    const divWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    if (x == 0) {
      shadowTarget.classList.remove("inset-scroll-shadow-not-at-left");
    }

    if (x > 0) {
      shadowTarget.classList.add("inset-scroll-shadow-not-at-left");
    }

    if (x < divWidth) {
      shadowTarget.classList.add("inset-scroll-shadow-not-at-right");
    }

    if (x == divWidth) {
      shadowTarget.classList.remove("inset-scroll-shadow-not-at-right");
    }
  }
}
